/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   LakehouseScheduleCell.tsx
 *     Feature         :   286 - FCM licence enforcement and estate limits (T159 items 20 and 26)
 *     Description     :   The lakehouse list's Schedule cell: the lakehouse's ACTIVE backup schedules
 *                         (dbo.backup_schedules, s.is_active = 1, the executor's rule) and the earliest next run, in
 *                         Eastern time. Until item 26 it showed dbo.lakehouses.backup_frequency / schedule_time,
 *                         migration 119 defaults nothing writes: "daily at 02:00 UTC" on every lakehouse while 0 of
 *                         198 schedules were active on dev (2026-10-22).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React from 'react';
import { AppTooltip } from '@/components/ui';
import { formatEastern } from '@/utils/dateFormatter';

export interface LakehouseSchedule {
  activeSchedules: number;
  /** ISO; null when no active schedule has a next run. */
  nextRunAt: string | null;
  cron: string | null;
  timezone: string | null;
}

export interface LakehouseScheduleCellProps {
  schedule: LakehouseSchedule | null | undefined;
}

export function LakehouseScheduleCell({ schedule }: LakehouseScheduleCellProps): React.ReactElement {
  if (!schedule || schedule.activeSchedules <= 0) {
    return <span className="text-[var(--color-text-secondary)]">No active schedule</span>;
  }
  const title = schedule.cron
    ? `Schedule ${schedule.cron}${schedule.timezone ? ` (${schedule.timezone})` : ''}`
    : 'Active backup schedule';
  return (
    <AppTooltip title={title}>
      <div>
        <div className="text-[var(--color-text-primary)]">
          {schedule.nextRunAt ? `Next ${formatEastern(schedule.nextRunAt)}` : 'Active, no next run recorded'}
        </div>
        {schedule.activeSchedules > 1 && (
          <div className="text-xs text-[var(--color-text-secondary)]">{schedule.activeSchedules} active schedules</div>
        )}
      </div>
    </AppTooltip>
  );
}
